import { useQuery, UseQueryResult, UseQueryOptions, QueryKey } from "@tanstack/react-query";
import { v4 as uuidv4 } from "uuid";

import { useSafeAuth0GetAccessToken } from "../../../hooks/useSafeAuth0GetAccessToken";

export type FetchQueryParams<TQueryResult, TData, TError = Error, TPayload = void> = {
  queryKey: QueryKey;
  url: string;
  requestHeaders?: HeadersInit;
  options?: Omit<UseQueryOptions<TQueryResult, TError, TData>, "queryKey" | "queryFn">;
  method?: RequestInit["method"];
  skipAuthHeaders?: boolean;
  body?: TPayload;
  getAccessToken?: () => Promise<string>;
};

// returns query options
export function getFetchQuery<TQueryResult, TData = TQueryResult, TError = Error, TPayload = void>({
  queryKey,
  url,
  options = {},
  requestHeaders = {},
  method = "GET",
  skipAuthHeaders = false,
  body,
  getAccessToken,
}: FetchQueryParams<TQueryResult, TData, TError, TPayload>): UseQueryOptions<TQueryResult, TError, TData> {
  const queryFn = async () => {
    let authToken: string | undefined;
    if (!skipAuthHeaders && getAccessToken) authToken = await getAccessToken();
    const response = await fetch(url, {
      method,
      headers: {
        ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
        "X-REQUEST-ID": uuidv4(),
        "Content-Type": "application/json",
        ...requestHeaders,
      },
      body: body ? JSON.stringify(body) : undefined,
    });
    if (!response.ok) {
      const errorResponse = (await response.json()) as { message: string } | undefined;
      throw new Error(errorResponse?.message || response.statusText);
    }
    return response.json() as Promise<TQueryResult>;
  };
  return { queryKey, queryFn, ...options };
}

// returns hook to use in component
export function useFetchQuery<TQueryResult, TData = TQueryResult, TError = Error, TPayload = void>(
  params: Omit<FetchQueryParams<TQueryResult, TData, TError, TPayload>, "getAccessToken">,
): UseQueryResult<TData, TError> {
  const { getAccessTokenSilently } = useSafeAuth0GetAccessToken();
  const queryOptions = getFetchQuery<TQueryResult, TData, TError, TPayload>({
    ...params,
    getAccessToken: getAccessTokenSilently,
  });
  return useQuery<TQueryResult, TError, TData>(queryOptions);
}
